<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <div :class="$style.header">
      <h1 :class="$style.title">🚨 신고 관리</h1>
      <p :class="$style.subtitle">사용자가 신고한 게시글, 댓글, 답글을 관리할 수 있습니다.</p>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="신고 목록을 불러오는 중..." />
    </div>

    <!-- 신고 관리 내용 -->
    <div v-else :class="$style.content">
      <!-- 요약 카드 -->
      <div :class="$style.summary">
        <div :class="$style.summaryCard">
          <h3>전체 신고</h3>
          <p :class="$style.summaryNumber">{{ totalReports }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>대기 중</h3>
          <p :class="$style.summaryNumber">{{ pendingReports }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>검토 완료</h3>
          <p :class="$style.summaryNumber">{{ reviewedReports }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>해결 완료</h3>
          <p :class="$style.summaryNumber">{{ resolvedReports }}</p>
        </div>
      </div>

      <!-- 필터 및 검색 -->
      <div :class="$style.controls">
        <div :class="$style.filterGroup">
          <select v-model="statusFilter" :class="$style.filterSelect">
            <option value="all">전체 상태</option>
            <option value="pending">대기 중</option>
            <option value="reviewed">검토 완료</option>
            <option value="resolved">해결 완료</option>
          </select>
          <select v-model="typeFilter" :class="$style.filterSelect">
            <option value="all">전체 유형</option>
            <option value="post">게시글</option>
            <option value="comment">댓글</option>
            <option value="recomment">답글</option>
          </select>
          <select v-model="reasonFilter" :class="$style.filterSelect">
            <option value="all">전체 사유</option>
            <option value="spam">스팸/광고</option>
            <option value="inappropriate">부적절한 내용</option>
            <option value="harassment">괴롭힘/욕설</option>
            <option value="fake">허위 정보</option>
            <option value="copyright">저작권 침해</option>
            <option value="other">기타</option>
          </select>
        </div>
        <div :class="$style.searchBox">
          <input v-model="searchQuery" type="text" placeholder="신고자 또는 내용으로 검색..." :class="$style.searchInput" />
        </div>
      </div>

      <!-- 신고 목록 -->
      <div v-if="filteredReports.length === 0" :class="$style.noData">
        <p>표시할 신고가 없습니다.</p>
      </div>
      <div v-else :class="$style.reportList">
        <div v-for="report in filteredReports" :key="report.id"
          :class="[$style.reportItem, $style[`status-${report.status}`]]">
          <div :class="$style.reportHeader">
            <div :class="$style.reportInfo">
              <span :class="$style.reportType">
                {{ getTypeLabel(report.documentType) }}
              </span>
              <span :class="$style.reportReason">
                {{ getReasonLabel(report.reason) }}
              </span>
              <span :class="$style.reportDate">
                {{ formatDate(report.reportedAt) }}
              </span>
            </div>
            <div :class="$style.reportStatus">
              <select v-model="report.status" :class="[$style.statusSelect, $style[`status-${report.status}`]]"
                @change="updateReportStatus(report.id, report.status)">
                <option value="pending">대기 중</option>
                <option value="reviewed">검토 완료</option>
                <option value="resolved">해결 완료</option>
              </select>
            </div>
          </div>

          <div :class="$style.reportContent">
            <div :class="$style.reportDetails">
              <p><strong>신고자:</strong> {{ report.reporterNickname }} ({{ report.reporter }})</p>
              <p v-if="report.description"><strong>신고 내용:</strong> {{ report.description }}</p>
            </div>
            <div :class="$style.reportActions">
              <button :class="$style.viewButton" @click="viewReportDetail(report)">
                상세 보기
              </button>
              <button :class="$style.deleteButton" @click="deleteReport(report.id)"
                :disabled="deletingReports.includes(report.id)">
                {{ deletingReports.includes(report.id) ? "삭제 중..." : "신고 삭제" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 신고 상세 모달 -->
    <div v-if="showDetailModal" :class="$style.modal">
      <div :class="$style.modalContent">
        <div :class="$style.modalHeader">
          <h3>신고 상세 정보</h3>
          <button @click="closeDetailModal" :class="$style.closeButton">×</button>
        </div>
        <div :class="$style.modalBody">
          <div v-if="selectedReport" :class="$style.detailContent">
            <div :class="$style.detailSection">
              <h4>신고 정보</h4>
              <p><strong>유형:</strong> {{ getTypeLabel(selectedReport.documentType) }}</p>
              <p><strong>사유:</strong> {{ getReasonLabel(selectedReport.reason) }}</p>
              <p><strong>신고일:</strong> {{ formatDate(selectedReport.reportedAt) }}</p>
              <p><strong>상태:</strong> {{ getStatusLabel(selectedReport.status) }}</p>
            </div>

            <div :class="$style.detailSection">
              <h4>신고자 정보</h4>
              <p><strong>닉네임:</strong> {{ selectedReport.reporterNickname }}</p>
              <p><strong>이메일:</strong> {{ selectedReport.reporter }}</p>
            </div>

            <div v-if="selectedReport.description" :class="$style.detailSection">
              <h4>신고 내용</h4>
              <p>{{ selectedReport.description }}</p>
            </div>

            <div :class="$style.detailSection">
              <h4>신고된 문서 경로</h4>
              <p :class="$style.documentPath">{{ selectedReport.documentPath }}</p>
            </div>
          </div>
        </div>
        <div :class="$style.modalFooter">
          <button v-if="canNavigateToReport(selectedReport)" @click="navigateToReportedContent(selectedReport)"
            :class="$style.navigateButton">
            신고된 글 보기
          </button>
          <button @click="closeDetailModal" :class="$style.cancelButton">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "/src/store/user.js";
import StatsTabs from "/src/components/StatsTabs.vue";
import LoadingScreen from "/src/components/LoadingScreen.vue";
import { updateReportStatus as updateReportStatusUtil, deleteReport as deleteReportUtil } from "@/utils/write/report";

export default {
  name: "ReportManagement",
  components: {
    StatsTabs,
    LoadingScreen,
  },
  data() {
    return {
      loading: true,
      reports: [],
      statusFilter: "all",
      typeFilter: "all",
      reasonFilter: "all",
      searchQuery: "",
      showDetailModal: false,
      selectedReport: null,
      deletingReports: [],
      userStore: useUserStore(),
    };
  },
  computed: {
    totalReports() {
      return this.reports.length;
    },
    pendingReports() {
      return this.reports.filter((r) => r.status === "pending").length;
    },
    reviewedReports() {
      return this.reports.filter((r) => r.status === "reviewed").length;
    },
    resolvedReports() {
      return this.reports.filter((r) => r.status === "resolved").length;
    },
    filteredReports() {
      let filtered = this.reports;

      // 상태 필터
      if (this.statusFilter !== "all") {
        filtered = filtered.filter((r) => r.status === this.statusFilter);
      }

      // 유형 필터
      if (this.typeFilter !== "all") {
        filtered = filtered.filter((r) => r.documentType === this.typeFilter);
      }

      // 사유 필터
      if (this.reasonFilter !== "all") {
        filtered = filtered.filter((r) => r.reason === this.reasonFilter);
      }

      // 검색
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            r.reporterNickname.toLowerCase().includes(query) ||
            r.reporter.toLowerCase().includes(query) ||
            (r.description && r.description.toLowerCase().includes(query))
        );
      }

      return filtered.sort((a, b) => {
        const aDate = a.reportedAt?.toDate ? a.reportedAt.toDate() : new Date(a.reportedAt);
        const bDate = b.reportedAt?.toDate ? b.reportedAt.toDate() : new Date(b.reportedAt);
        return bDate - aDate; // 최신순
      });
    },
  },
  async mounted() {
    // 관리자 권한 확인
    if (this.userStore.auth !== "admin") {
      this.$alert("관리자만 접근할 수 있습니다.");
      this.$router.push("/");
      return;
    }

    await this.loadReports();
  },
  methods: {
    async loadReports() {
      try {
        this.loading = true;
        const reportsRef = collection(firestore, "reports");
        const q = query(reportsRef, orderBy("reportedAt", "desc"));
        const reportsSnapshot = await getDocs(q);

        this.reports = reportsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("신고 목록 로드 오류:", error);
        this.$alert("신고 목록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },

    async updateReportStatus(reportId, newStatus) {
      try {
        await updateReportStatusUtil(reportId, newStatus);
        this.$alert("신고 상태가 업데이트되었습니다.");
      } catch (error) {
        console.error("신고 상태 업데이트 오류:", error);
        this.$alert("상태 업데이트 중 오류가 발생했습니다.");
        // 실패 시 원래 상태로 되돌리기
        await this.loadReports();
      }
    },

    async deleteReport(reportId) {
      const confirmed = await this.$confirm("이 신고를 삭제하시겠습니까?");
      if (!confirmed) {
        return;
      }

      try {
        this.deletingReports.push(reportId);
        await deleteReportUtil(reportId);
        this.$alert("신고가 삭제되었습니다.");
        await this.loadReports();
      } catch (error) {
        console.error("신고 삭제 오류:", error);
        this.$alert("신고 삭제 중 오류가 발생했습니다.");
      } finally {
        this.deletingReports = this.deletingReports.filter((id) => id !== reportId);
      }
    },

    viewReportDetail(report) {
      this.selectedReport = report;
      this.showDetailModal = true;
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedReport = null;
    },

    getTypeLabel(type) {
      const labels = {
        post: "게시글",
        comment: "댓글",
        recomment: "답글",
      };
      return labels[type] || type;
    },

    getReasonLabel(reason) {
      const labels = {
        spam: "스팸/광고",
        inappropriate: "부적절한 내용",
        harassment: "괴롭힘/욕설",
        fake: "허위 정보",
        copyright: "저작권 침해",
        other: "기타",
      };
      return labels[reason] || reason;
    },

    getStatusLabel(status) {
      const labels = {
        pending: "대기 중",
        reviewed: "검토 완료",
        resolved: "해결 완료",
      };
      return labels[status] || status;
    },

    formatDate(timestamp) {
      if (!timestamp) return "날짜 정보 없음";

      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return "날짜 정보 없음";
      }
    },

    // 신고된 콘텐츠로 이동 가능한지 확인
    canNavigateToReport(report) {
      if (!report || !report.documentPath) return false;

      // boards로 시작하는 경우만 이동 가능 (게시글, 댓글, 답글)
      return report.documentPath.startsWith("boards/");
    },

    // 신고된 콘텐츠로 이동
    navigateToReportedContent(report) {
      if (!this.canNavigateToReport(report)) return;

      try {
        const path = report.documentPath;

        // boards/{boardId} 형식에서 boardId 추출
        const boardIdMatch = path.match(/^boards\/([^/]+)/);
        if (!boardIdMatch) return;

        const boardId = boardIdMatch[1];

        let route = "";
        let hash = "";

        if (report.documentType === "post") {
          // 게시글 신고: 게시글 상세 페이지로 이동
          route = `/board/${boardId}`;
        } else if (report.documentType === "comment") {
          // 댓글 신고: 상위 게시글로 이동하고 해당 댓글로 스크롤
          route = `/board/${boardId}`;
          const commentIdMatch = path.match(/comments\/([^/]+)$/);
          if (commentIdMatch) {
            hash = `#comment-${commentIdMatch[1]}`;
          }
        } else if (report.documentType === "recomment") {
          // 답글 신고: 상위 게시글로 이동하고 해당 댓글로 스크롤
          route = `/board/${boardId}`;
          const commentIdMatch = path.match(/comments\/([^/]+)\/recomments/);
          if (commentIdMatch) {
            hash = `#comment-${commentIdMatch[1]}`;
          }
        }

        if (!route) {
          this.$alert("해당 콘텐츠로 이동할 수 없습니다.");
          return;
        }

        // 새 창에서 열기
        const fullUrl = `${window.location.origin}${route}${hash}`;
        window.open(fullUrl, "_blank");
      } catch (error) {
        console.error("신고된 콘텐츠로 이동 중 오류:", error);
        this.$alert("해당 콘텐츠로 이동할 수 없습니다.");
      }
    },
  },
};
</script>

<style module>
/* ========================================
   반응형 디자인 (최대 1024px)
   - Medium: 481px ~ (기본 스타일)
   - Small: ~ 480px
   ======================================== */

.container {
  width: 100%;
  max-width: 1024px;
  padding: 0 15px;
  margin: 0 auto;
  min-height: 100vh;
}

.header {
  background: white;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 12px;
  text-align: center;
}

.title {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.content {
  background: white;
  border-radius: 10px;
  padding: 15px;
}

/* 요약 카드 */
.summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summaryCard {
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  color: white;
  padding: 15px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.summaryCard h3 {
  margin: 0 0 6px 0;
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.9;
}

.summaryNumber {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
}

/* 필터 및 검색 */
.controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filterGroup {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filterSelect {
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  background: white;
  min-width: 110px;
}

.filterSelect:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.searchBox {
  flex: 1;
  min-width: 220px;
}

.searchInput {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.searchInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

/* 신고 목록 */
.noData {
  text-align: center;
  padding: 50px 20px;
  color: #999;
  font-style: italic;
}

.reportList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reportItem {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 18px;
  background: white;
  transition: all 0.2s ease;
}

.reportItem:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.reportItem.status-pending {
  border-left: 4px solid #ffc107;
}

.reportItem.status-reviewed {
  border-left: 4px solid #17a2b8;
}

.reportItem.status-resolved {
  border-left: 4px solid #28a745;
}

.reportHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.reportInfo {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.reportType {
  background: #66bdff;
  color: white;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
}

.reportReason {
  background: #ff6b9d;
  color: white;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
}

.reportDate {
  color: #666;
  font-size: 12px;
}

.statusSelect {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.statusSelect.status-pending {
  background: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.statusSelect.status-reviewed {
  background: #d1ecf1;
  border-color: #17a2b8;
  color: #0c5460;
}

.statusSelect.status-resolved {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.reportContent {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  flex-wrap: wrap;
}

.reportDetails {
  flex: 1;
  min-width: 250px;
}

.reportDetails p {
  margin: 0 0 6px 0;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

.reportActions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.viewButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.viewButton:hover {
  background: #4a9eff;
}

.deleteButton {
  background: #dc3545;
  color: white;
  border: none;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteButton:hover:not(:disabled) {
  background: #c82333;
}

.deleteButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 모달 */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modalContent {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 550px;
  max-height: 75vh;
  overflow-y: auto;
}

.modalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px;
  border-bottom: 1px solid #e9ecef;
}

.modalHeader h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeButton:hover {
  color: #333;
}

.modalBody {
  padding: 18px;
}

.detailContent {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detailSection {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
}

.detailSection:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detailSection h4 {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.detailSection p {
  margin: 0 0 6px 0;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

.documentPath {
  background: #f8f9fa;
  padding: 8px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}

.modalFooter {
  display: flex;
  justify-content: flex-end;
  padding: 18px;
  border-top: 1px solid #e9ecef;
}

.cancelButton {
  background: #6c757d;
  color: white;
  border: none;
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.cancelButton:hover {
  background: #5a6268;
}

.navigateButton {
  background: #28a745;
  color: white;
  border: none;
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-right: 10px;
}

.navigateButton:hover {
  background: #218838;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px;
  }

  .header {
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .title {
    font-size: 1.5rem;
    margin-bottom: 6px;
  }

  .subtitle {
    font-size: 0.85rem;
  }

  .content {
    padding: 15px;
    border-radius: 8px;
  }

  .summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .summaryCard {
    padding: 12px 8px;
    border-radius: 8px;
  }

  .summaryCard h3 {
    font-size: 0.75rem;
    margin-bottom: 6px;
  }

  .summaryNumber {
    font-size: 1.4rem;
  }

  .controls {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }

  .filterGroup {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .filterSelect {
    width: 100%;
    padding: 10px 12px;
    font-size: 13px;
    min-width: auto;
  }

  .searchBox {
    min-width: auto;
    width: 100%;
  }

  .searchInput {
    padding: 10px 12px;
    font-size: 13px;
  }

  .noData {
    padding: 40px 15px;
    font-size: 0.9rem;
  }

  .reportList {
    gap: 10px;
  }

  .reportItem {
    padding: 12px;
    border-radius: 6px;
  }

  .reportHeader {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: 8px;
  }

  .reportInfo {
    flex-wrap: wrap;
    gap: 8px;
  }

  .reportType,
  .reportReason {
    padding: 3px 8px;
    font-size: 10px;
    border-radius: 12px;
  }

  .reportDate {
    font-size: 11px;
    width: 100%;
  }

  .reportStatus {
    width: 100%;
  }

  .statusSelect {
    width: 100%;
    padding: 8px 10px;
    font-size: 12px;
    border-radius: 5px;
  }

  .reportContent {
    flex-direction: column;
    gap: 12px;
  }

  .reportDetails {
    min-width: auto;
    width: 100%;
  }

  .reportDetails p {
    font-size: 12px;
    margin-bottom: 5px;
  }

  .reportActions {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .viewButton,
  .deleteButton {
    width: 100%;
    padding: 10px 14px;
    font-size: 13px;
    border-radius: 6px;
  }

  /* 모달 */
  .modal {
    align-items: flex-end;
  }

  .modalContent {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
    margin: 0;
  }

  .modalHeader {
    padding: 15px;
  }

  .modalHeader h3 {
    font-size: 16px;
  }

  .closeButton {
    font-size: 20px;
    width: 26px;
    height: 26px;
  }

  .modalBody {
    padding: 15px;
  }

  .detailContent {
    gap: 15px;
  }

  .detailSection {
    padding-bottom: 12px;
  }

  .detailSection h4 {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .detailSection p {
    font-size: 12px;
    margin-bottom: 5px;
  }

  .documentPath {
    padding: 8px;
    font-size: 11px;
    border-radius: 4px;
  }

  .modalFooter {
    padding: 15px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .cancelButton,
  .navigateButton {
    width: 100%;
    padding: 12px 18px;
    font-size: 14px;
    border-radius: 8px;
    margin: 0;
  }
}
</style>
